import { useFillsGraphConfigurationSelector } from 'widgets/fillsGraph/store/configuration/selectors';
import { useSelectLocked } from 'layoutRenderer/store/selectors';
import {
    CompanionChartTypes,
    FillsChartType,
    FillsGraphConfiguration,
} from 'widgets/fillsGraph/store/configuration/FillsGraphConfiguration';
import { FillsGraphPortfolioData } from 'widgets/fillsGraph/fillsGraphPortfolioData';
import { FillsGraphData } from 'widgets/fillsGraph/fillsGraphData';
import {
    FillsGraphLegendStatus,
    useLegendStatus,
} from 'widgets/fillsGraph/legendStatus';
import { useMemo } from 'react';

export enum CompanionChartVisibilityStatus {
    // Both legend and chart is visible
    Visible,
    // Legend is visible, but chart is collapsed using "Hide" toggle button
    Collapsed,
    // Legend and chart are both hidden so entire companion chart is hidden
    Hidden,
}

export interface CompanionChartVisibility {
    type: CompanionChartTypes;
    visibility: CompanionChartVisibilityStatus;
}

export function useCompanionChartVisibilities(
    fillsGraphData: FillsGraphData,
    fillsGraphPortfolioData: FillsGraphPortfolioData
): CompanionChartVisibility[] {
    const config = useFillsGraphConfigurationSelector();
    const locked = useSelectLocked();
    const legendStatus = useLegendStatus(
        fillsGraphData,
        fillsGraphPortfolioData
    );

    return useMemo(() => {
        return getComponentChartVisibilities(
            fillsGraphData,
            fillsGraphPortfolioData,
            config,
            legendStatus,
            locked
        );
    }, [fillsGraphData, fillsGraphPortfolioData, config, legendStatus, locked]);
}

function getComponentChartVisibilities(
    fillsGraphData: FillsGraphData,
    fillsGraphPortfolioData: FillsGraphPortfolioData,
    config: FillsGraphConfiguration,
    legendStatus: FillsGraphLegendStatus,
    locked: boolean
): CompanionChartVisibility[] {
    const { hasFullOrderData } = fillsGraphPortfolioData;
    const { visibleCharts } = config;

    return [
        {
            type: FillsChartType.ExpectedVsActual,
            visibility: getExpectedVsActualVisibility(),
        },
        {
            type: FillsChartType.PercentageOfMarketVolume,
            visibility: getPercentageOfMarketVolumeVisibility(),
        },
        { type: FillsChartType.PnL, visibility: getPnLVisibility() },
        {
            type: FillsChartType.PerformanceVsIndex,
            visibility: getPerformanceVsIndexVisibility(),
        },
    ];
    function getExpectedVsActualVisibility() {
        return getVisibilityFromConfig(
            FillsChartType.ExpectedVsActual,
            legendStatus.allExpectedVsActualHidden
        );
    }

    function getPercentageOfMarketVolumeVisibility() {
        if (!hasFullOrderData) {
            return CompanionChartVisibilityStatus.Hidden;
        }
        return getVisibilityFromConfig(
            FillsChartType.PercentageOfMarketVolume,
            legendStatus.allPercentageOfMarketVolumeHidden
        );
    }

    function getPnLVisibility() {
        if (!hasFullOrderData) {
            return CompanionChartVisibilityStatus.Hidden;
        }
        return getVisibilityFromConfig(
            FillsChartType.PnL,
            legendStatus.allPlChartHidden
        );
    }

    function getPerformanceVsIndexVisibility() {
        return getVisibilityFromConfig(
            FillsChartType.PerformanceVsIndex,
            legendStatus.allPerformanceVsIndexChartHidden
        );
    }

    function getVisibilityFromConfig(
        chartType: CompanionChartTypes,
        allLegendsHidden: boolean
    ) {
        const hidden = !visibleCharts.some((x) => x.type === chartType);
        const collapsed = config[chartType].collapsed;

        if (hidden) {
            return CompanionChartVisibilityStatus.Hidden;
        }

        if (collapsed) {
            if (locked && allLegendsHidden) {
                return CompanionChartVisibilityStatus.Hidden;
            }
            return CompanionChartVisibilityStatus.Collapsed;
        }

        return CompanionChartVisibilityStatus.Visible;
    }
}

export const getCompanionChartVisibility = (
    visibilities: CompanionChartVisibility[],
    chartType: CompanionChartTypes
) => visibilities.find((v) => v.type === chartType)!.visibility;

export function getLastVisibleChart(
    visibilities: CompanionChartVisibility[]
): FillsChartType {
    const isCompanionChartVisible = (c: CompanionChartTypes) =>
        getCompanionChartVisibility(visibilities, c) ===
        CompanionChartVisibilityStatus.Visible;

    if (isCompanionChartVisible(FillsChartType.PerformanceVsIndex)) {
        return FillsChartType.PerformanceVsIndex;
    }

    if (isCompanionChartVisible(FillsChartType.PnL)) {
        return FillsChartType.PnL;
    }

    if (isCompanionChartVisible(FillsChartType.PercentageOfMarketVolume)) {
        return FillsChartType.PercentageOfMarketVolume;
    }

    if (isCompanionChartVisible(FillsChartType.ExpectedVsActual)) {
        return FillsChartType.ExpectedVsActual;
    }

    return FillsChartType.OrderFills;
}

export const getIsOrderFillsVisible = (config: FillsGraphConfiguration) =>
    config.visibleCharts.some((f) => f.type === FillsChartType.OrderFills);

export const isCompanionChartVisible = (
    companionChartVisibilities: CompanionChartVisibility[],
    t: CompanionChartTypes
) =>
    getCompanionChartVisibility(companionChartVisibilities, t) !==
    CompanionChartVisibilityStatus.Hidden;

export const isThereACompanionChartVisible = (
    companionChartVisibilities: CompanionChartVisibility[]
) =>
    isCompanionChartVisible(
        companionChartVisibilities,
        FillsChartType.ExpectedVsActual
    ) ||
    isCompanionChartVisible(companionChartVisibilities, FillsChartType.PnL) ||
    isCompanionChartVisible(
        companionChartVisibilities,
        FillsChartType.PerformanceVsIndex
    ) ||
    isCompanionChartVisible(
        companionChartVisibilities,
        FillsChartType.PercentageOfMarketVolume
    );
